import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthStore } from 'src/core/stores';
import { LanguageStore } from 'src/core/stores/language.store';
import {
  BlogEntryDto,
  CoreBlogEntriesService,
  CreateBlogEntryDto,
  UpdateBlogEntryDto,
} from 'src/generated/api/nestjs-backend';
import { DialogComponent } from '../../shared/dialog/dialog.component';
import { WysiwygEditorComponent } from '../../shared/wysiwyg-editor/wysiwyg-editor.component';

@Component({
  selector: 'core-edit-blogpost-dialog',
  templateUrl: './edit-blogpost-dialog.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoDirective,
    ReactiveFormsModule,
    WysiwygEditorComponent,
    DialogComponent,
  ],
})
export class EditBlogpostDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private elementRef = inject(ElementRef);
  private languageStore = inject(LanguageStore);
  private coreBlogEntriesService = inject(CoreBlogEntriesService);
  private authStore = inject(AuthStore);

  @ViewChild(WysiwygEditorComponent) wysiwygEditor!: WysiwygEditorComponent;
  @ViewChild(DialogComponent) dialogComponent!: DialogComponent;
  @Output() save: EventEmitter<unknown> = new EventEmitter<unknown>();
  @Input() newsItem!: BlogEntryDto;
  @Input() createOrEditDialog!: boolean;
  private onClose = new EventEmitter<boolean>();
  public newsItemDialogTitle = 'edit-blogpost-dialog-title';
  public newsItemDialogSubtitle = 'edit-blogpost-dialog-subtitle';
  public newsItemDialogConfirmButtonText = 'core.buttons.save';
  public newsItemDialogCancelButtonText = 'core.buttons.cancel';
  public blogPostForm!: FormGroup;
  public blogPostContent!: string | null;
  ngOnInit() {
    if (!this.createOrEditDialog) {
      this.newsItemDialogTitle = 'delete-blogpost-dialog-title';
      this.newsItemDialogSubtitle = 'delete-blogpost-dialog-subtitle';
      this.newsItemDialogConfirmButtonText = 'core.buttons.delete';
      this.newsItemDialogCancelButtonText = 'core.buttons.cancel';
    } else {
      this.initFormValidation();
    }
  }

  ngOnDestroy() {
    this.elementRef.nativeElement.remove();
  }

  ngAfterViewInit() {
    this.dialogComponent.onOpen();
  }

  onSave() {
    if (this.createOrEditDialog) {
      const editorContent = this.wysiwygEditor.getEditorContent();
      if (this.newsItem.uuid) {
        this.updateBlogEntry(editorContent);
      } else {
        this.createBlogEntry(editorContent);
      }
    } else {
      this.deleteBlogEntry();
    }
  }

  closeModal(success: boolean) {
    this.onClose.emit(success);
  }

  get blogPostFormTitle() {
    return this.blogPostForm.get('blogPostFormTitle');
  }

  private initFormValidation() {
    if (this.newsItem) {
      this.blogPostForm = new FormGroup({
        blogPostFormTitle: new FormControl(this.newsItem.title, {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(64),
          ],
          updateOn: 'blur',
        }),
      });
    }
    this.blogPostContent = this.languageStore.translateDto(
      this.newsItem.content,
    )();
  }

  // TODO Add translations to update blog entry
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private createBlogEntry(editorContent: string) {
    //TODO remove the hardcoded calls for blogUuid
    const createdNewsItem: CreateBlogEntryDto = {
      published: true,
      publishedAt: new Date().toISOString(),
      authorUserUuid: this.newsItem.authorUserUuid!,
      blogUuid: this.newsItem.blogUuid!,
    };

    this.coreBlogEntriesService
      .createBlogEntry({
        xTenantSlug: this.authStore.tenantSlug(),
        createBlogEntryDto: createdNewsItem,
      })
      .subscribe({
        next: () => {
          this.closeModal(true);
        },
        error: (error) => {
          console.error(
            'Error while closing the dialog window: ',
            error.error?.message,
          );
        },
      });
  }

  // TODO Add translations to update blog entry
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private updateBlogEntry(editorContent: string) {
    const updatedNewsItem: UpdateBlogEntryDto = {
      authorUserUuid: this.newsItem.authorUserUuid!,
    };

    this.coreBlogEntriesService
      .updateBlogEntry({
        xTenantSlug: this.authStore.tenantSlug(),
        blogEntryUuid: this.newsItem?.uuid,
        updateBlogEntryDto: updatedNewsItem,
      })
      .subscribe({
        next: () => {
          this.closeModal(true);
        },
        error: (error) => {
          console.error(
            'Error while closing the dialog window: ',
            error.error?.message,
          );
        },
      });
  }

  private deleteBlogEntry() {
    this.coreBlogEntriesService
      .deleteBlogEntry({
        xTenantSlug: this.authStore.tenantSlug(),
        blogEntryUuid: this.newsItem?.uuid,
      })
      .subscribe({
        next: () => {
          this.closeModal(true);
        },
        error: (error) => {
          console.error(
            'Error while closing the dialog window: ',
            error.error?.message,
          );
        },
      });
  }
}
